import { verifyPasswordOTP } from '../../../services/auth-service';
import CustomInput from '../../../components/shared/custom-input';
import Button from '../../../components/shared/custom-btn';
import React, { useEffect, useState } from 'react';
import { validateForm } from '../../../utils/form-validator-utils';
import { useNavigate } from 'react-router-dom';

const OtpVerify = ({ formData, setFormData, errors, setErrors, setStep, handleChange }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [redirecting, setRedirecting] = useState(false); // ✅ for controlling 3-second lock
  const navigate = useNavigate();

  useEffect(() => {
    const timeout = setTimeout(() => {
      const keys = localStorage.getItem('passKeys');
      if (!keys) {
        setStep(1);
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [setStep]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { errors: validationErrors, isValid } = validateForm(
      {
        otp: formData.otp,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      },
      ['otp', 'password', 'confirmPassword']
    );

    setErrors({ ...validationErrors, apiError: '' });
    setSuccessMessage('');
    if (!isValid) return;

    setIsSubmitting(true);
    try {
      const response = await verifyPasswordOTP({
        code: formData.otp,
        verificationKey: localStorage.getItem('passKeys'),
        newPassword: formData.password
      });

      if (response.success) {
        localStorage.removeItem('passKeys');
        setSuccessMessage('✅ Password updated successfully! Redirecting to login...');
        setRedirecting(true); // ✅ lock UI

        const timeout = setTimeout(() => {
          navigate('/');
        }, 4000);

        return () => clearTimeout(timeout); // cleanup
      } else {
        setErrors(prev => ({
          ...prev,
          apiError: response.data.message || 'OTP verification failed. Please try again.'
        }));
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        apiError: error.message || 'An unexpected error occurred.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImmediateRedirect = () => {
    navigate('/');
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      {successMessage && (
        <div className="mb-2 p-3 bg-green-100 text-green-700 text-sm rounded-lg text-center">
          {successMessage}
          <br />
          <span
            className="underline cursor-pointer text-blue-700 mt-1 inline-block"
            onClick={handleImmediateRedirect}
          >
            Click here to login
          </span>
        </div>
      )}

      {errors.apiError && (
        <div className="mb-2 p-3 bg-red-100 text-red-700 text-sm rounded-lg text-center">
          {errors.apiError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        <CustomInput
          label="Enter OTP Code"
          name="otp"
          type="text"
          value={formData.otp}
          onChange={handleChange}
          inputStyle={`w-full text-center border !py-2 border-border ${redirecting ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"}  rounded-lg ${errors.otp ? 'border-red-500' : ''}`}
          placeholder="Enter 6-digit code"
          error={errors.otp}
          onlyNumbers={true}
          maxLength={6}
          disabled={redirecting}

        />

        <div className="flex flex-col gap-3">
          <CustomInput
            label="New Password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            inputStyle={`border border-border !py-2 ${redirecting ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"} rounded-lg`}
            placeholder="At least 8 characters"
            type="password"
            error={errors.password}
            disabled={redirecting}
          />
          <CustomInput
            label="Confirm Password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            inputStyle={`border border-border !py-2 ${redirecting ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"} rounded-lg`}
            placeholder="Re-enter your password"
            type="password"
            error={errors.confirmPassword}
            disabled={redirecting}
          />
        </div>

        <Button
          type="submit"
          label={isSubmitting ? 'Updating...' : 'Update Password'}
          loading={isSubmitting}
          disabled={isSubmitting || redirecting}
          className={`w-full bg-blue-800 mt-5 text-white font-medium py-2 px-4 rounded-lg ${isSubmitting || redirecting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-900'}`}
        />
      </form>
    </div>
  );
};

export default OtpVerify;
